import React, { useState, useRef, useEffect } from "react";
import "./Sidebar.css";
// import relayLogo from "../assets/relay.png";
import appnameImg from "../assets/appname.png";
import {
  FaThLarge,
  FaRegFileAlt,
  FaTasks,
  FaFolderOpen,
  FaProjectDiagram,
  FaUsers,
  FaUserShield,
  FaBars,
  FaTimes,
  FaUser, // Import FaTimes
} from "react-icons/fa";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { GrPlan } from "react-icons/gr";


import { useNavigate, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import ChangePasswordModal from "./ChangePasswordModal";
import getApiUrl from "../utils/api";
import { usePostHog } from "posthog-js/react";

const sidebarLinks = [
  { icon: <FaThLarge />, label: "Dashboard" },
  { icon: <FaRegFileAlt />, label: "Summarize" },
  { icon: <FaTasks />, label: "My Task" },
  { icon: <FaFolderOpen />, label: "Deputed Task" },
  { icon: <FaProjectDiagram />, label: "Project" },
  { icon: <FaUsers />, label: "Groups" },
  //{ icon: <FaUserShield />, label: "Roles" },
  {
    icon: <FaRegFileAlt />,
    label: "Daily Checklist",
    submenu: [
      { icon: <GrPlan />, label: "Planning", link: "/yesno?tab=planning" },
      { icon: <IoMdCheckmarkCircleOutline />, label: "Execution", link: "/yesno?tab=execution" }
    ]
  },
];

const getLinkPath = (label) => {
  switch (label) {
    case "Dashboard":
      return "/dashboard";
    case "Summarize":
      return "/summarize";
    case "My Task":
      return "/mytask";
    case "Deputed Task":
      return "/task";
    case "Groups":
      return "/groups";
    case "Project":
      return "/project";
    case "Roles":
      return "/roles";
    case "Daily Checklist":
      return "/yesno";
    default:
      return "";
  }
};

const Sidebar = ({ sidebarOpen, setSidebarOpen, isMobile }) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null); // Generic: stores which menu is open
  const [submenuPositions, setSubmenuPositions] = useState({}); // Generic: stores positions
  const userMenuRef = useRef(null);
  const dropdownRef = useRef(null);
  const submenuRefs = useRef({}); // Generic: refs for all submenus
  const menuItemRefs = useRef({}); // Generic: refs for all menu items
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState({});
  const [showChangePassword, setShowChangePassword] = useState(false);

  const posthog = usePostHog();

  useEffect(() => {
    fetch(getApiUrl("api/auth/me"), {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.user) {
          const userDetails = data.user;
          setUser(userDetails);

          posthog.identify(userDetails?.userId, {
            email: userDetails?.email,
            departmentId: userDetails?.department_id,
          })
        }
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
      });
  }, [posthog]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      // Generic: Close any open submenu when clicking outside
      if (openSubmenu && submenuRefs.current[openSubmenu]) {
        const submenuEl = submenuRefs.current[openSubmenu];
        if (submenuEl && !submenuEl.contains(event.target)) {
          // Check if click is not on the menu item itself
          const clickedElement = event.target.closest('.sidebar-link');
          const isMenuItemClick = clickedElement?.textContent?.includes(openSubmenu);
          if (!isMenuItemClick) {
            setOpenSubmenu(null);
          }
        }
      }
    }
    if (userMenuOpen || openSubmenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuOpen, openSubmenu]);

  // Animate dropdown
  useEffect(() => {
    if (userMenuOpen && dropdownRef.current) {
      const options = Array.from(
        dropdownRef.current.querySelectorAll(".sidebar-user-dropdown-option")
      );
      gsap.fromTo(
        dropdownRef.current,
        { y: 32, scale: 0.92, opacity: 0 },
        { y: 0, scale: 1, opacity: 1, duration: 0.32, ease: "back.out(1.7)" }
      );
      gsap.fromTo(
        options,
        { y: 18, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.22, stagger: 0.07, ease: "power2.out" },
        "-=0.18"
      );
    }
  }, [userMenuOpen]);

  const handleLogout = async () => {
    try {
      setUserMenuOpen(false);

      // Call logout API to clear server-side cookie
      await fetch(getApiUrl("api/auth/logout"), {
        method: "POST",
        credentials: "include",
      });

      // Clear local storage
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("user");
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("rememberedEmail");

      // Navigate to login page
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      // Even if API call fails, clear local storage and navigate
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("user");
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("rememberedEmail");
      navigate("/");
    }
  };

  const handleLinkClick = (label) => {
    // Find the link configuration
    const link = sidebarLinks.find(l => l.label === label);

    // Generic: Check if this menu item has a submenu
    if (link && link.submenu) {
      // Calculate position for collapsed mode
      if (!sidebarOpen && menuItemRefs.current[label]) {
        const rect = menuItemRefs.current[label].getBoundingClientRect();
        setSubmenuPositions(prev => ({ ...prev, [label]: { top: rect.top } }));
      }
      // Toggle the submenu
      setOpenSubmenu(prev => prev === label ? null : label);
      return; // Don't navigate or close sidebar
    }

    // Regular navigation for items without submenus
    if (label === "Dashboard") {
      navigate("/dashboard");
    } else if (label === "Summarize") {
      navigate("/summarize");
    } else if (label === "My Task") {
      navigate("/mytask");
    } else if (label === "Deputed Task") {
      navigate("/task");
    } else if (label === "Groups") {
      navigate("/groups");
    } else if (label === "Project") {
      navigate("/project");
    } else if (label === "Roles") {
      navigate("/roles");
    } else if (label === "Daily Checklist") {
      navigate("/yesno");
    }

    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Handler for password change
  const handleChangePassword = async ({
    oldPassword,
    newPassword,
    confirmPassword,
  }) => {
    try {
      // Use the current user state instead of localStorage
      if (!user || !user.userId) {
        window.alert(
          "User information not available. Please refresh the page."
        );
        return;
      }

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/auth/change-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include", // Include cookies for authentication
          body: JSON.stringify({
            userId: user.userId,
            oldPassword,
            newPassword,
            confirmPassword,
          }),
        }
      );
      const data = await res.json();
      if (data.success) {
        window.alert("Password changed successfully!");
        setShowChangePassword(false);
      } else {
        window.alert(data.message || "Failed to change password.");
      }
    } catch (err) {
      window.alert("Network error. Please try again.");
    }
  };

  return (
    <>
      <aside
        className={`sidebar${sidebarOpen ? " sidebar-open" : " collapsed"}${isMobile ? " mobile" : ""
          }`}
      >
        <div className="sidebar-content">
          <div className="sidebar-top-row">
            {sidebarOpen && (
              <div className="sidebar-header">
                <img
                  src={appnameImg}
                  alt="App Name"
                  className="sidebar-app-name"
                />
              </div>
            )}
            {!isMobile && (
              <button
                className="sidebar-hamburger"
                onClick={() => setSidebarOpen((open) => !open)}
              >
                <FaBars />
              </button>
            )}
            {isMobile && sidebarOpen && (
              <button
                className="sidebar-hamburger"
                onClick={() => setSidebarOpen(false)}
                style={{ marginLeft: "auto" }}
              >
                <FaTimes />
              </button>
            )}
          </div>

          <nav className="sidebar-nav">
            {sidebarLinks.map((link) => (
              <div
                key={link.label}
                ref={el => { if (link.submenu) menuItemRefs.current[link.label] = el; }}
                style={{
                  position: "relative",
                  zIndex: openSubmenu === link.label ? 100 : "auto"
                }}>
                <div
                  className={`sidebar-link${location.pathname === getLinkPath(link.label) ? " active" : ""
                    }`}
                  onClick={() => handleLinkClick(link.label)}
                >
                  <span className="sidebar-icon">{link.icon}</span>
                  {sidebarOpen && (
                    <span className="sidebar-label">{link.label}</span>
                  )}
                  {link.submenu && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: "12px",
                        transition: "transform 0.2s",
                        transform: openSubmenu === link.label ? "rotate(180deg)" : "rotate(0)",
                        display: sidebarOpen ? "inline" : "none"
                      }}
                    >
                      ▼
                    </span>
                  )}
                </div>

                {/* Generic Submenu Dropdown */}
                {link.submenu && openSubmenu === link.label && (
                  <div
                    ref={el => { submenuRefs.current[link.label] = el; }}
                    className={`submenu-dropdown ${sidebarOpen ? 'expanded' : 'collapsed'}`}
                    style={{
                      top: !sidebarOpen ? `${submenuPositions[link.label]?.top || 0}px` : undefined
                    }}
                  >
                    {link.submenu.map((subItem, index) => (
                      <button
                        key={index}
                        className="submenu-item"
                        onClick={() => {
                          navigate(subItem.link);
                          setOpenSubmenu(null);
                          if (isMobile) setSidebarOpen(false);
                        }}
                      >
                        {subItem.icon} {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div
            className="sidebar-user"
            ref={userMenuRef}
            onClick={() => setUserMenuOpen((open) => !open)}
            style={{ cursor: "pointer", position: "relative" }}
          >
            <FaUser />
            {sidebarOpen && (
              <div>
                <div className="sidebar-user-name">
                  {user.FirstName || user.email || "User"}
                </div>
              </div>
            )}
            {userMenuOpen && (
              <div
                ref={dropdownRef}
                className={`user-menu-dropdown ${sidebarOpen ? 'expanded' : 'collapsed'}`}
              >
                {sidebarOpen && (
                  <div className="user-menu-arrow" />
                )}
                <button
                  className={!sidebarOpen ? "user-menu-item" : "sidebar-user-dropdown-option"}
                  style={sidebarOpen ? {
                    background: "none",
                    border: "none",
                    outline: "none",
                    padding: "12px 28px 10px 28px",
                    fontSize: 17,
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "background 0.18s",
                    color: "#222",
                    borderRadius: 10,
                  } : undefined}
                  onMouseOver={sidebarOpen ? (e) => (e.currentTarget.style.background = "#f3f0ff") : undefined}
                  onMouseOut={sidebarOpen ? (e) => (e.currentTarget.style.background = "none") : undefined}
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate("/myprofile");
                  }}
                >
                  My Profile
                </button>
                <button
                  className={!sidebarOpen ? "user-menu-item" : "sidebar-user-dropdown-option"}
                  style={sidebarOpen ? {
                    background: "none",
                    border: "none",
                    outline: "none",
                    padding: "12px 28px 10px 28px",
                    fontSize: 17,
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "background 0.18s",
                    color: "#222",
                    borderRadius: 10,
                  } : undefined}
                  onMouseOver={sidebarOpen ? (e) => (e.currentTarget.style.background = "#f3f0ff") : undefined}
                  onMouseOut={sidebarOpen ? (e) => (e.currentTarget.style.background = "none") : undefined}
                  onClick={() => {
                    setUserMenuOpen(false);
                    setShowChangePassword(true);
                  }}
                >
                  Change Password
                </button>
                <button
                  className={!sidebarOpen ? "user-menu-item" : "sidebar-logout-btn sidebar-user-dropdown-option"}
                  style={sidebarOpen ? {
                    background: "none",
                    border: "none",
                    outline: "none",
                    padding: "12px 28px 10px 28px",
                    fontSize: 17,
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "background 0.18s",
                    color: "#e53935",
                    fontWeight: 600,
                    borderRadius: 10,
                  } : undefined}
                  onClick={handleLogout}
                  onMouseOver={sidebarOpen ? (e) =>
                    (e.currentTarget.style.background = "#fbe9e7") : undefined
                  }
                  onMouseOut={sidebarOpen ? (e) =>
                    (e.currentTarget.style.background = "none") : undefined
                  }
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
      <ChangePasswordModal
        open={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onSave={handleChangePassword}
      />
    </>
  );
};

export default Sidebar;
